import { queryOptions } from '@tanstack/react-query';

import { userApi } from './user.api';

export const userQueryKeys = {
    sessionReady: ['user', 'session-ready'] as const,
    passwordStatus: ['user', 'password-status'] as const,
    emailVerificationStatus: ['user', 'email-verification-status'] as const,
};

// Thrown while provisioning is still in flight so React Query keeps retrying; any other error
// (e.g. the apiClient's 401 -> sign-in redirect, or a 5xx) is terminal and must not be retried.
class SessionPendingError extends Error {}

export function sessionReadyOptions() {
    return queryOptions({
        queryKey: userQueryKeys.sessionReady,
        queryFn: async ({ signal }) => {
            const { ready } = await userApi.finalizeSignIn(signal);
            if (!ready) {
                throw new SessionPendingError();
            }
            return { ready };
        },
        retry: (_failureCount, error) => error instanceof SessionPendingError,
        retryDelay: 500,
        gcTime: 0,
    });
}

export function passwordStatusOptions() {
    return queryOptions({
        queryKey: userQueryKeys.passwordStatus,
        queryFn: ({ signal }) => userApi.getPasswordStatus(signal),
    });
}

export function emailVerificationStatusOptions() {
    return queryOptions({
        queryKey: userQueryKeys.emailVerificationStatus,
        queryFn: ({ signal }) => userApi.getEmailVerificationStatus(signal),
        // The link is consumed out of band, so a cached "unverified" can already be wrong — only a
        // cached "verified" is safe to trust.
        staleTime: (query) => (query.state.data?.verified ? Number.POSITIVE_INFINITY : 0),
        // fetchQuery registers no observer, so the default gcTime would drop a trusted "verified"
        // and make the gate pay a blocking round-trip on the next navigation.
        gcTime: Number.POSITIVE_INFINITY,
    });
}
